import { Bot, Building2, Cable, LayoutGrid, Landmark, Rss, Settings, type LucideIcon } from 'lucide-react'
import type { Feature } from '@/lib/types'

export interface NavLink {
  id: string
  /** i18n key */
  title: string
  url: string
  requiredFeature?: Feature
}

export interface NavEntry extends Partial<NavLink> {
  id: string
  title: string
  icon: LucideIcon
  children?: NavLink[]
}

export interface NavGroup {
  id: string
  title: string
  adminOnly?: boolean
  items: NavEntry[]
}

// Same tree and gates as v1 src/menu-items
export const navigation: NavGroup[] = [
  {
    id: 'main',
    title: 'v2.nav.workspace',
    items: [
      { id: 'dashboard', title: 'menu.dashboard.items.default', url: '/', icon: LayoutGrid },
      { id: 'feed', title: 'menu.dashboard.items.feed', url: '/feed', icon: Rss },
      { id: 'companies', title: 'menu.dashboard.items.companies', url: '/companies', icon: Building2 },
      { id: 'ai-chat', title: 'aiChat.title', url: '/ai-chat', icon: Bot },
    ],
  },
  {
    id: 'admin',
    title: 'v2.nav.administration',
    adminOnly: true,
    items: [
      {
        id: 'settings',
        title: 'menu.settings.items.settings',
        icon: Settings,
        children: [
          { id: 'team', title: 'menu.settings.items.team', url: '/settings/team' },
          { id: 'custom-fields', title: 'menu.settings.items.customFields', url: '/settings/custom-fields', requiredFeature: 'custom_fields' },
          { id: 'mcp', title: 'v2.nav.mcp', url: '/settings/mcp' },
          // open on every plan; Free teams get a lock here and a read-only page
          { id: 'slack', title: 'v2.nav.slack', url: '/settings/slack', requiredFeature: 'crm_integrations' },
          { id: 'ai', title: 'v2.nav.ai', url: '/settings/ai' },
          { id: 'subscription', title: 'menu.settings.items.subscription', url: '/settings/subscription' },
        ],
      },
      {
        id: 'city-settings',
        title: 'v2.nav.citySettings',
        icon: Landmark,
        children: [
          // open on every plan; Free teams get a lock here and a read-only page
          { id: 'alerts', title: 'v2.alerts.title', url: '/city-settings/alerts', requiredFeature: 'alerts' },
        ],
      },
      {
        id: 'crm',
        title: 'menu.settings.items.crmIntegrations',
        icon: Cable,
        children: [
          { id: 'crm-overview', title: 'menu.settings.items.overview', url: '/settings/crm-integrations' },
          { id: 'hubspot', title: 'menu.settings.items.hubspot', url: '/settings/hubspot', requiredFeature: 'crm_integrations' },
          { id: 'zapier', title: 'menu.settings.items.zapier', url: '/settings/zapier', requiredFeature: 'crm_integrations' },
        ],
      },
    ],
  },
]

/** Flat lookup for page titles in the header. */
export function findNavTitle(pathname: string): string | undefined {
  if (pathname === '/settings') return 'menu.settings.items.settings'
  if (pathname === '/profile') return 'v2.profile.title'
  for (const group of navigation) {
    for (const item of group.items) {
      if (item.url === pathname) return item.title
      const child = item.children?.find((c) => pathname === c.url || pathname.startsWith(c.url + '/'))
      if (child) return child.title
      if (item.url && item.url !== '/' && pathname.startsWith(item.url + '/')) return item.title
    }
  }
  return undefined
}
